import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";
import { clearAcquisitionMetadata, onCookieSettingsOpen, readCookieConsent, writeCookieConsent } from "utilities";

import styles from "./CookieConsentBanner.module.css";

/**
 * Cookie consent (TTAPI-47, GDPR / ePrivacy Art. 5(3)). Shown to every visitor until they choose, and
 * again whenever the footer's "Cookie settings" asks for it. Accept loads Google Analytics and lets the
 * first-visit attribution be stored; Reject keeps both off and clears anything already stored. Reject
 * is as prominent as Accept, so refusing is as easy as agreeing.
 */
const CookieConsentBanner = () => {
	const { t } = useTranslation("common");
	const [isOpen, setIsOpen] = useState(() => readCookieConsent() === null);

	useEffect(() => onCookieSettingsOpen(() => setIsOpen(true)), []);

	const onAccept = useCallback((): void => {
		writeCookieConsent("granted");
		setIsOpen(false);
	}, []);

	const onReject = useCallback((): void => {
		writeCookieConsent("denied");
		clearAcquisitionMetadata();
		setIsOpen(false);
	}, []);

	if (!isOpen) {
		return null;
	}

	return (
		<section aria-labelledby="cookie-consent-title" className={styles.banner}>
			<div className={styles.text}>
				<h2 className={styles.title} id="cookie-consent-title">
					{t("cookieConsent.title")}
				</h2>
				<p className={styles.body}>
					{t("cookieConsent.body")}{" "}
					<Link className={styles.policyLink} to={Paths.cookiePolicy}>
						{t("cookieConsent.policyLink")}
					</Link>
				</p>
			</div>
			<div className={styles.actions}>
				<button className={styles.secondaryButton} onClick={onReject} type="button">
					{t("cookieConsent.reject")}
				</button>
				<button className={styles.primaryButton} onClick={onAccept} type="button">
					{t("cookieConsent.accept")}
				</button>
			</div>
		</section>
	);
};

export default CookieConsentBanner;
